import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cancelProposal, createVersion, updateProposal } from '@mkcore/proposals/schema';
import { Button, Card, ErrorState, PageHeader, Skeleton } from '@mkcore/ui';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { ReminderButton } from '@/features/collab/reminders';
import { useCan } from '@/lib/session';
import { formatDate, formatDateTime } from '@/lib/format';
import { ConfirmButton } from '@/components/confirm';
import { EngagementSelect } from '@/features/engagements/engagement-select';
import { MailCard } from '@/features/proposals/mail-card';
import { DecisionDialog, Lines, PdfUpload, ProposalStatusBadge, SendDialog, VersionStatusBadge, VersionText, eur, proposalQuery } from '@/features/proposals/proposals';

export const Route = createFileRoute('/_app/proposals/$id')({ component: ProposalPage });

/**
 * One proposal (CO-0595, CO-1350): the current version with its text, lines, totals and PDF, sending it, the decision of
 * the client, and the earlier versions. A send that failed shows why, and sending again retries it (#273).
 */
function ProposalPage() {
  const { id } = Route.useParams();
  const { t, i18n } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('proposals:update');
  const q = useQuery(proposalQuery(id));
  const [dialog, setDialog] = useState<'send' | 'decision' | null>(null);
  const newVersion = useActionMutation(createVersion, { invalidate: [['proposal']] });
  const cancel = useActionMutation(cancelProposal, { invalidate: [['proposal']] });
  const update = useActionMutation(updateProposal, { invalidate: [['proposal']], success: t('common.saved') });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const p = q.data;
  const [v, ...older] = p.versions;
  const locale = i18n.language === 'en' ? 'en' : 'nl';
  const draft = canUpdate && v!.status === 'draft';
  const open = p.status === 'draft' || p.status === 'sent';

  return (
    <>
      <PageHeader
        eyebrow={p.number}
        title={p.title}
        description={`${p.organisationName} · ${t(`proposals.type.${p.type}`)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ProposalStatusBadge status={p.status} />
            <ReminderButton targetType="proposal" targetId={p.id} title={`${p.number} ${p.title}`} path={`/proposals/${p.id}`} dates={[{ label: t('reminders.validUntil'), date: v!.validUntil }]} />
            {draft && (
              <Button disabled={!v!.pdfFileId || !v!.lines.length} onClick={() => setDialog('send')}>
                {v!.sendError ? t('proposals.sendAgain') : t('proposals.send')}
              </Button>
            )}
            {canUpdate && v!.status === 'sent' && open && <Button onClick={() => setDialog('decision')}>{t('proposals.recordDecision')}</Button>}
            {draft && open && (
              <Button variant="secondary" disabled={!v!.lines.length} onClick={() => setDialog('decision')}>
                {t('proposals.recordDecision')}
              </Button>
            )}
            {canUpdate && (v!.status === 'sent' || p.status === 'rejected' || p.status === 'expired') && (
              <Button variant="secondary" loading={newVersion.isPending} onClick={() => newVersion.mutate({ input: { id: p.id } })}>
                {t('proposals.newVersion')}
              </Button>
            )}
            {canUpdate && open && (
              <ConfirmButton
                label={t('proposals.cancel')}
                variant="danger"
                title={t('proposals.cancelTitle', { number: p.number })}
                description={t('proposals.cancelText')}
                confirmLabel={t('proposals.cancel')}
                onConfirm={() => cancel.mutateAsync({ input: { id: p.id } })}
              />
            )}
          </div>
        }
      />
      <div className="mb-4 flex flex-wrap gap-4 text-sm text-fg-2">
        <Link to="/org/$id" params={{ id: p.organisationId }} className="underline">
          {p.organisationName}
        </Link>
        {p.leadId && (
          <Link to="/crm/leads/$id" params={{ id: p.leadId }} className="underline">
            {t('proposals.lead')}
          </Link>
        )}
        {p.engagementId && (
          <Link to="/engagements/$id" params={{ id: p.engagementId }} className="underline">
            {t('proposals.engagement')}
          </Link>
        )}
        {canUpdate && (
          // Link the proposal to an engagement that already exists, or change it (#120).
          <EngagementSelect
            organisationId={p.organisationId}
            value={p.engagementId}
            disabled={update.isPending}
            onChange={(engagementId) => update.mutate({ input: { id: p.id, engagementId: engagementId || null }, ifMatch: p.version })}
          />
        )}
        {p.contactName && <span>{t('proposals.to', { name: p.contactName })}</span>}
        {p.decisionNote && <span>{p.decisionNote}</span>}
      </div>
      {v!.sendError && (
        <Card className="mb-4 border-danger/40 bg-danger-bg p-4 text-sm text-danger" role="alert">
          <strong>{t('proposals.sendFailed')}:</strong> {v!.sendError}
          <p className="mt-1">{v!.status === 'draft' ? t('proposals.sendFailedHint') : t('proposals.sendFailedAdmin')}</p>
        </Card>
      )}
      <div className="grid gap-4 xl:grid-cols-[3fr_2fr]">
        <Card className="p-4">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-bold">
            {t('proposals.versionN', { n: v!.version })} <VersionStatusBadge status={v!.status} />
          </h2>
          <Lines key={`${v!.id}-lines`} v={v!} currency={p.currency} editable={draft} />
        </Card>
        <Card className="grid content-start gap-3 p-4">
          <h2 className="text-lg font-bold">{t('proposals.document')}</h2>
          {v!.pdfFileId ? (
            <a href={`/api/v1/files/${v!.pdfFileId}/content`} target="_blank" rel="noreferrer" className="text-sm underline">
              {t('proposals.openPdf')} {v!.renderedAt && `(${formatDateTime(v!.renderedAt)})`}
            </a>
          ) : (
            <p className="text-sm text-fg-3">{t('proposals.noPdf')}</p>
          )}
          {draft && <PdfUpload v={v!} />}
          {v!.sentAt && <p className="text-sm">{t('proposals.sentOn', { at: formatDateTime(v!.sentAt) })}</p>}
          {v!.viewedAt && <p className="text-sm">{t('proposals.viewedOn', { at: formatDateTime(v!.viewedAt) })}</p>}
          {v!.decidedVia === 'portal' && v!.decidedAt && (
            <p className="text-sm">{t('proposals.decidedInPortal', { decision: t(`proposals.status.${v!.status}`), name: v!.decidedByName ?? '', at: formatDateTime(v!.decidedAt), ip: v!.decidedIp ?? '?' })}</p>
          )}
          <VersionText key={`${v!.id}-${v!.rowVersion}`} v={v!} editable={draft} />
        </Card>
      </div>
      <div className="mt-4">
        <MailCard key={`${v!.id}-mail-${v!.rowVersion}`} v={v!} editable={draft} />
      </div>
      {older.length > 0 && (
        <section className="mt-6" aria-label={t('proposals.olderVersions')}>
          <h2 className="mb-2 text-lg font-bold">{t('proposals.olderVersions')}</h2>
          <ul className="grid gap-1 text-sm">
            {older.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-2">
                {t('proposals.versionN', { n: o.version })} <VersionStatusBadge status={o.status} /> {eur(o.totalCents, p.currency, locale)}
                {o.sentAt && <span className="text-fg-3">· {formatDate(o.sentAt)}</span>}
                {o.pdfFileId && (
                  <a href={`/api/v1/files/${o.pdfFileId}/content`} target="_blank" rel="noreferrer" className="underline">
                    PDF
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {dialog === 'send' && <SendDialog v={v!} onClose={() => setDialog(null)} />}
      {dialog === 'decision' && <DecisionDialog v={v!} onClose={() => setDialog(null)} />}
    </>
  );
}
